import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Users, HardHat, Truck, FolderOpen, Wallet, CornerDownLeft,
  ArrowUp, ArrowDown, X, LayoutDashboard, Plane, Banknote, KeyRound,
  CalendarCheck, Briefcase,
} from 'lucide-react';
import { supabase } from '@/core/supabase';
import { useSession } from '@/core/session';
import { useBackToClose } from '@/app/useAndroidBack';
import { Spinner } from './Loader';
import { cn } from './ui';
import { useI18n, useT, type TranslationKey } from '@/core/i18n';
import { docLabel } from '@/core/reports/docStrings';
import { ADMIN_NAV } from '@/app/AppShell';
import { SECTIONS as SETTINGS_SECTIONS } from '@/modules/settings/SettingsLayout';
/* The English dictionary itself, which is where the English names already live.
   A second map of key to English title would be a third copy of the same
   strings — and the copy that goes stale, since nothing would fail when it
   did. `en` is in the main bundle regardless: it is the fallback every other
   language falls back to. */
import { en } from '@/core/i18n/en';
import { jsonShape } from '@/core/jsonShape';

/* =============================================================================
 * Global search
 * -----------------------------------------------------------------------------
 * One box that reaches everything: an employee by name, code, phone, Emirates
 * ID or passport; a job by number; a plate; a document; a payroll period. Plus
 * the screens themselves, so it doubles as a way to navigate without the menu.
 *
 * Two things make it fast enough to use while typing:
 *
 *   · one server call, not one per entity — the SQL does a UNION ALL with a
 *     per-group cap, so adding a sixth entity costs nothing on the wire
 *   · navigation matches locally and appears instantly, while the server
 *     results fill in behind them
 * ========================================================================== */

interface SearchHit {
  kind:
    | 'employee' | 'project' | 'vehicle' | 'document' | 'payroll'
    | 'leave' | 'advance' | 'payslip' | 'login' | 'trip' | 'holiday'
    | 'designation'
    | 'page';
  id: string;
  title: string;
  subtitle: string;
  meta: string;
  url: string;
}

const KIND_META: Record<SearchHit['kind'], { labelKey: TranslationKey; icon: typeof Users }> = {
  employee: { labelKey: 'cp.employees', icon: Users },
  project: { labelKey: 'cp.projects', icon: HardHat },
  vehicle: { labelKey: 'cp.vehicles', icon: Truck },
  document: { labelKey: 'cp.documents', icon: FolderOpen },
  payroll: { labelKey: 'cp.payroll', icon: Wallet },
  leave: { labelKey: 'cp.leave', icon: Plane },
  advance: { labelKey: 'cp.advances', icon: Banknote },
  payslip: { labelKey: 'cp.payslips', icon: Wallet },
  login: { labelKey: 'cp.logins', icon: KeyRound },
  trip: { labelKey: 'cp.trips', icon: Truck },
  holiday: { labelKey: 'cp.holidays', icon: CalendarCheck },
  designation: { labelKey: 'cp.designations', icon: Briefcase },
  page: { labelKey: 'cp.goTo', icon: LayoutDashboard },
};

/* =============================================================================
 * The screens
 * -----------------------------------------------------------------------------
 * Built from the same two lists the sidebar and the settings shell draw from,
 * rather than written out again here.
 *
 * It was written out again here, and it drifted exactly as a second copy always
 * does: somebody typed "backup" into this box and was told there was nothing,
 * while the Backups screen sat in the settings menu two clicks away. Nine
 * screens were missing by then — security, email, notifications, self-check,
 * the import, both logs, the company details and the backups.
 *
 * Now a screen is searchable by existing. Adding one to the sidebar or to the
 * settings shell is the whole of the work.
 *
 * -----------------------------------------------------------------------------
 * WHY THERE ARE ALSO KEYWORDS
 * -----------------------------------------------------------------------------
 * A person searches for what they want, not for what the screen is called. They
 * type "restore" for Backups, "password" for Security, "2FA" for the same,
 * "excel" for Import. Matching the title alone answers none of those, and the
 * title is what somebody already knows the name of — which is the case where
 * they did not need to search.
 *
 * Only in English. These are the words somebody types quickly, and an Arabic
 * reader typing an Arabic word matches the translated title, which is the same
 * mechanism from the other side.
 * ========================================================================== */
const KEYWORDS: Record<string, string> = {
  '/settings/backups': 'backup copy restore snapshot archive nightly',
  '/settings/security': 'password 2fa mfa two factor authenticator lock login',
  '/settings/users': 'login account username password permission role access',
  '/settings/email': 'mail smtp sender notification send',
  '/settings/notifications': 'alert bell push notify rules who',
  '/settings/import': 'excel csv spreadsheet upload bulk migrate',
  '/settings/sign-ins': 'login history who signed in audit',
  '/settings/activity': 'audit log history changes trail',
  '/settings/self-check': 'diagnostic health test problem broken',
  '/settings/holidays': 'holiday eid national day off',
  '/settings/rules': 'policy overtime rate wage rule gratuity',
  '/settings/company': 'name address licence logo trade currency timezone',
  '/settings/appearance': 'theme colour color dark light language',
  '/payroll': 'salary wage wps pay run payslip',
  '/advances': 'loan advance repayment deduction',
  '/leaves': 'holiday absence annual sick leave',
  '/fleet': 'vehicle car truck trip driver plate',
  '/attendance': 'present absent shift hours in out',
  '/rollcall': 'roll call muster headcount',
  '/reports': 'export excel pdf download',
  '/documents': 'passport visa emirates id contract file',
  '/projects': 'site job number geofence location',
  '/employees': 'staff worker labour people person',
};

interface PageEntry {
  title: string;
  url: string;
  permission: string | null;
  /** What the screen is called in English, for matching whatever the reader's
      language is — an Arabic reader still types "payroll" half the time. */
  english: string;
  keywords: string;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const lang = useI18n().language === 'ar' ? 'ar' : 'en';
  const navigate = useNavigate();
  const { can } = useSession();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useBackToClose(open, onClose);

  // 180ms: long enough that a fast typist sends one query rather than eight,
  // short enough that it never feels like waiting.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 180);
    return () => window.clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (open) {
      setTerm('');
      setDebounced('');
      setActive(0);
      // A frame's delay, or the dialog's own entry animation steals focus back.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    enabled: open && debounced.length >= 2,
    // Results are stable for a moment, so arrowing back and forth does not
    // refire the query.
    staleTime: 30_000,
    queryFn: async (): Promise<SearchHit[]> => {
      const { data: rows, error } = await supabase.rpc('global_search', {
        p_query: debounced,
        p_limit: 4,
      });
      if (error) throw error;
      return jsonShape<SearchHit[]>((rows ?? []));
    },
  });

  const pages: PageEntry[] = useMemo(() => {
    const settingsUrl = (to: string) => `/settings/${to}`;

    return [
      ...ADMIN_NAV.map((item) => ({
        title: t(item.labelKey),
        english: en[item.labelKey] ?? item.labelKey,
        url: item.to,
        permission: item.permission,
        keywords: KEYWORDS[item.to] ?? '',
      })),
      ...SETTINGS_SECTIONS.map((section) => ({
        title: t(section.labelKey),
        english: en[section.labelKey] ?? section.labelKey,
        url: settingsUrl(section.to),
        /* Appearance is everybody's — it is where the light and dark choice
           lives. The rest of the settings shell is for whoever manages it. */
        permission: section.to === 'appearance' ? null : 'settings.manage',
        keywords: KEYWORDS[settingsUrl(section.to)] ?? '',
      })),
      /* Screens with no entry in either menu. A destination somebody can reach
         by clicking is a destination they should be able to reach by typing. */
      { title: t('me.title'), english: en['me.title'], url: '/me',
        permission: null, keywords: 'my own me self portal payslip' },
    ];
  }, [t]);

  const pageHits: SearchHit[] = useMemo(() => {
    if (debounced.length < 2) return [];
    const needle = debounced.toLowerCase();

    return pages
      .filter((page) => {
        if (page.permission && !can(page.permission)) return false;
        /* Three ways in: what the screen is called in the reader's language,
           what it is called in English, and the words somebody actually types
           when they do not know either. */
        return page.title.toLowerCase().includes(needle)
          || page.english.toLowerCase().includes(needle)
          || page.keywords.includes(needle);
      })
      .slice(0, 5)
      .map((page) => ({
        kind: 'page' as const,
        id: page.url + page.title,
        title: page.title,
        subtitle: '',
        meta: '',
        url: page.url,
      }));
  }, [debounced, can, pages]);

  // Screens first — they resolve instantly and are usually what a short term
  // means. Records follow in the order the server ranked them.
  const hits = useMemo(() => [...pageHits, ...(data ?? [])], [pageHits, data]);

  const grouped = useMemo(() => {
    const groups = new Map<SearchHit['kind'], SearchHit[]>();
    for (const hit of hits) {
      const list = groups.get(hit.kind) ?? [];
      list.push(hit);
      groups.set(hit.kind, list);
    }
    return [...groups.entries()];
  }, [hits]);

  const choose = useCallback((hit: SearchHit) => {
    navigate(hit.url);
    onClose();
  }, [navigate, onClose]);

  // Keyboard: arrows move, Enter opens, Escape closes.
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose(); return; }
      if (hits.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive((i) => (i + 1) % hits.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((i) => (i - 1 + hits.length) % hits.length);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const hit = hits[active];
        if (hit) choose(hit);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hits, active, choose, onClose]);

  useEffect(() => { setActive(0); }, [debounced]);

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  let index = -1;

  return (
    // Anchored to the top of the viewport, which on a phone now begins behind
    // the clock — hence the additive top inset rather than a flat `p-4`.
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pb-4 pt-safe-plus-4 sm:pt-[12vh]">
      <button
        type="button"
        aria-label={t('cp.closeSearch')}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-enter"
      />

      <div
        role="dialog"
        aria-label={t('shell.search')}
        className={cn(
          'relative w-full max-w-xl bg-surface border border-line rounded-card shadow-pop',
          // `max-h-full` resolves against the wrapper's content box, which has
          // already had the insets taken out of it above — so the sheet can
          // never grow back into the space that padding just reserved.
          'flex flex-col overflow-hidden max-h-full sm:max-h-[70dvh] sheet-enter',
        )}
      >
        {/* ---- input ------------------------------------------------------ */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-line shrink-0">
          <Search size={18} className="text-subtle shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={t('cp.placeholder')}
            className="flex-1 bg-transparent outline-none text-[16px] sm:text-[15px] placeholder:text-subtle min-w-0"
            autoComplete="off"
            spellCheck={false}
          />
          {isFetching && <Spinner size={15} className="text-subtle shrink-0" />}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('cp.close')}
            className="p-1.5 -me-1.5 rounded-card text-subtle hover:text-text hover:bg-surface-2 transition-colors shrink-0"
          >
            <X size={17} />
          </button>
        </div>

        {/* ---- results ---------------------------------------------------- */}
        <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
          {debounced.length < 2 ? (
            <Hint />
          ) : hits.length === 0 ? (
            <p className="px-4 py-10 text-center text-[13px] text-muted">
              {isFetching
                ? t('cp.searching')
                : t('cp.nothingFound', { query: debounced })}
            </p>
          ) : (
            grouped.map(([kind, items]) => {
              const meta = KIND_META[kind];
              return (
                <div key={kind} className="py-1.5">
                  <p className="px-4 py-1 text-[10px] uppercase tracking-widest text-subtle font-bold">
                    {t(meta.labelKey)}
                  </p>
                  {items.map((hit) => {
                    index += 1;
                    const isActive = index === active;
                    const Icon = meta.icon;

                    return (
                      <button
                        key={hit.id}
                        type="button"
                        data-active={isActive}
                        onMouseEnter={() => setActive(hits.indexOf(hit))}
                        onClick={() => choose(hit)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-2.5 text-start transition-colors',
                          isActive ? 'bg-brand-subtle' : 'hover:bg-surface-2',
                        )}
                      >
                        <span
                          className={cn(
                            'shrink-0 w-8 h-8 rounded-card grid place-items-center',
                            isActive ? 'bg-brand text-brand-fg' : 'bg-surface-3 text-muted',
                          )}
                        >
                          <Icon size={15} />
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="block text-[13.5px] font-semibold truncate">
                            {/* A page name is this system's word; a person's
                                name, a job number and a plate are not, and
                                docLabel leaves anything it does not know
                                exactly as it was written. */}
                            {docLabel(hit.title, lang)}
                          </span>
                          {hit.subtitle && (
                            <span className="block text-[11.5px] text-muted truncate">
                              {hit.subtitle}
                            </span>
                          )}
                        </span>

                        {hit.meta && (
                          <span className="shrink-0 text-[10.5px] text-subtle uppercase tracking-wide">
                            {hit.meta}
                          </span>
                        )}

                        {isActive && (
                          <CornerDownLeft size={13} className="shrink-0 text-brand" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* ---- footer ----------------------------------------------------- */}
        <div className="hidden sm:flex items-center gap-4 px-4 h-9 border-t border-line text-[10.5px] text-subtle shrink-0">
          <span className="flex items-center gap-1">
            <Key><ArrowUp size={9} /></Key><Key><ArrowDown size={9} /></Key>{t('cp.navigateKey')}</span>
          <span className="flex items-center gap-1">
            <Key><CornerDownLeft size={9} /></Key>{t('cp.openKey')}</span>
          <span className="flex items-center gap-1">
            <Key>esc</Key>{t('cp.closeKey')}</span>
        </div>
      </div>
    </div>
  );
}

function Hint() {
  const t = useT();
  return (
    <div className="px-4 py-8 text-center">
      <Search size={22} className="mx-auto text-subtle mb-2.5" />
      <p className="text-[13px] font-semibold">{t('cp.searchEverything')}</p>
      <p className="text-[11.5px] text-muted mt-1.5 leading-relaxed max-w-xs mx-auto">
        A name, an employee ID, a phone number, an Emirates ID, a passport, a job
        number, a plate — or the name of any screen.
      </p>
    </div>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded border border-line bg-surface-2 font-sans text-[10px]">
      {children}
    </kbd>
  );
}

/** Cmd/Ctrl+K anywhere, and `/` when not already typing. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      } else if (event.key === '/' && !typing) {
        event.preventDefault();
        setOpen(true);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return { open, setOpen };
}
